import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ClientExport } from '@salonomy/types';
import { api, expectOk, unwrap } from '../lib/api.js';

/**
 * GDPR controls (WS6 brief item 5). Four operations over the existing GDPR endpoints:
 *   GET    /v1/clients/{id}/export   → per-client Article-20 export bundle
 *   DELETE /v1/clients/{id}          → per-client erasure (manager-only, mirrors clients RBAC)
 *   GET    /v1/gdpr/book/export      → your whole client book (per-renter portability / shared book)
 *   DELETE /v1/gdpr/book             → erase YOUR sealed book (chair renter leaving)
 *
 * These are deliberately NOT TanStack queries: an export is a one-off download (we never want to
 * cache a client's full PII bundle in memory), and erasure is a one-off destructive write. Both are
 * modelled as mutations so the calling component gets isPending / error / explicit success states.
 *
 * ONLINE-ONLY (non-negotiable): GDPR erasure must never be offline-queued — a regulator-facing
 * "we deleted this person" promise cannot be made by an outbox that replays later. We call the
 * generated client directly (no offline-write wrapper); if the network is down the mutation rejects
 * and the UI shows a clear failure, never a queued "will sync" state. Exports are likewise live.
 *
 * RBAC mirrors the API exactly. Per-client export/erasure require manager (owner/manager); the UI
 * gates the buttons so a non-manager never gets a server 403 as first feedback. Book export/erasure
 * are scoped by the session: a chair renter reaches ONLY their own sealed book (RLS-enforced), and
 * book erasure is rejected server-side for anyone without a sealed book — the UI surfaces that too.
 */

/** BookExport is defined inline on the gdpr route (not re-exported from @salonomy/types); the shape
 *  comes back fully typed from the generated client via unwrap(), so callers infer it from here. */
export type BookExport = Awaited<ReturnType<typeof exportBook>>;

async function exportBook() {
  return unwrap(await api.GET('/v1/gdpr/book/export'));
}

/**
 * Export one client's full record (profile, appointments, redacted comms audit). Returns the bundle
 * for the caller to serialise to a downloadable file. Online-only by nature — a network failure
 * rejects rather than queues.
 */
export function useExportClient() {
  return useMutation({
    mutationFn: async (clientId: string): Promise<ClientExport> =>
      unwrap(await api.GET('/v1/clients/{id}/export', { params: { path: { id: clientId } } })),
  });
}

/**
 * Erase one client permanently (GDPR Article 17). Online-only: this is a hard delete the salon is
 * accountable for, never offline-queued. Invalidates the clients list on success so the erased
 * record disappears from every search page immediately.
 */
export function useEraseClient() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (clientId: string): Promise<void> =>
      expectOk(await api.DELETE('/v1/clients/{id}', { params: { path: { id: clientId } } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}

/**
 * Export the whole book the session can see: a chair renter's sealed book, or the shared book for an
 * owner/manager. Returns the bundle (scope + clients + appointments) for download. Online-only.
 */
export function useExportBook() {
  return useMutation({
    mutationFn: async (): Promise<BookExport> => exportBook(),
  });
}

/**
 * Erase YOUR sealed client book — the chair-renter-leaving / right-to-erasure path. The server
 * rejects this for anyone without a sealed book, so the UI only offers it to a renter session.
 * Returns the count deleted for an explicit, auditable success message. Online-only; invalidates the
 * clients list so the now-empty book reflects everywhere.
 */
export function useEraseBook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<{ deleted: number }> => unwrap(await api.DELETE('/v1/gdpr/book')),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['clients'] }),
  });
}
